import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ahProducten from '../data/ah-producten.json';
import { itemsApi } from '../features/boodschappen/items.api';
import type { Suggestie } from '../features/boodschappen/items.api';
import { voorraadApi } from '../features/voorraad/voorraad.api';
import { ApiError } from './apiClient';
import { useToast } from './useToast';

const AH_CATALOGUS: { naam: string; eenheid: string; zoektermen?: string[] }[] = ahProducten;

/**
 * Wat de boodschappen- en de voorraadpagina delen.
 *
 * De twee tabs zitten aan dezelfde data vast: "Is op" haalt iets uit de
 * voorraad en zet het op de lijst, en een vast product doorzetten raakt de
 * lijst. Daarom verversen alle mutaties allebei de lijsten — anders klopt de
 * andere tab niet meer zodra je erheen gaat. TanStack Query hergebruikt de
 * cache, dus dezelfde query in twee componenten kost geen tweede request.
 */
export function useLijsten() {
  const qc = useQueryClient();
  const toast = useToast();

  const items = useQuery({ queryKey: ['items'], queryFn: itemsApi.list });
  const voorraad = useQuery({ queryKey: ['voorraad'], queryFn: voorraadApi.list });

  const ververs = () => {
    void qc.invalidateQueries({ queryKey: ['items'] });
    void qc.invalidateQueries({ queryKey: ['voorraad'] });
    void qc.invalidateQueries({ queryKey: ['suggesties'] });
  };

  /** Elke mutatie faalt op dezelfde manier: melding tonen, lijsten opnieuw
   *  ophalen zodat het scherm weer klopt met de server. */
  const meldFout = (err: unknown) =>
    toast(err instanceof ApiError ? err.message : 'Er ging iets mis', true);

  return { items, voorraad, ververs, meldFout, toast };
}

/** Eerder gebruikte productnamen. Veranderen traag, dus een uur houdbaar. */
export function useSuggesties() {
  const query = useQuery({
    queryKey: ['suggesties'],
    queryFn: itemsApi.suggesties,
    staleTime: 60 * 60 * 1000,
  });

  // De AH-catalogus vult aan wat nog nooit besteld is; wat al eerder gebruikt
  // is, wint bij een naam die in allebei voorkomt. Het blijft een suggestie in
  // een <datalist> op een gewoon tekstveld — typen wat er niet bij staat kan
  // altijd nog.
  const suggesties = useMemo((): Suggestie[] => {
    const historie = query.data?.suggesties ?? [];
    const gezien = new Set(historie.map((s) => s.naam.toLowerCase()));
    const catalogus = AH_CATALOGUS.filter((p) => !gezien.has(p.naam.toLowerCase())).map((p) => ({
      ...p,
      keer: 0,
    }));
    return [...historie, ...catalogus];
  }, [query.data]);

  return { ...query, data: { suggesties } };
}
